import {FC, useMemo, useState} from 'react';
import dayjs from 'dayjs';
import Linkify from 'linkify-react';

// Shared UI Library Components
import {CustomTooltip, UserAvatar} from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './message.scss';
import {getFileIcon} from "../../../../utils/getFileIcon.utils";
import {truncateText} from "../../../../utils/fileName.utils";
import {PreviewFileModal} from "../../../../modals/preview-file-modal/preview-file-modal";

type AttachmentType = {
  size: number;
  type: string;
  url: string;
  name: string;
}

interface IMessageProps {
  isMy: boolean;
  message: string;
  avatarUrl?: string | null;
  name?: string;
  message_time?: string;
  attachments: AttachmentType[];
}

const IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];


export const Message: FC<IMessageProps> = (
  { isMy, message, avatarUrl, name = 'Consultant', message_time, attachments }: IMessageProps
) => {

  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);

  const imageAttachments = useMemo(() => {
    return attachments ? attachments.filter((att: AttachmentType) =>
      IMAGE_TYPES.includes(att.type.toLowerCase())
    ) : [];
  }, [attachments]);

  const imageUrls = useMemo(() => imageAttachments.map((att: AttachmentType) => att.url), [imageAttachments]);
  const imageNames = useMemo(() => imageAttachments.map((att: AttachmentType) => att.name), [imageAttachments]);

  const isImageFile = (type: string): boolean => {
    return IMAGE_TYPES.includes(type.toLowerCase());
  };

  const handleFileClick = (attachment: AttachmentType): void => {
    if (isImageFile(attachment.type)) {
      const imageIndex = imageAttachments.findIndex((img: AttachmentType) => img.url === attachment.url);
      setCurrentImageIndex(imageIndex);
      setPreviewModalOpen(true);
    } else {
      downloadFile(attachment);
    }
  };

  // Download file
  const downloadFile = (attachment: AttachmentType): void => {
    const link = document.createElement('a');
    link.href = attachment.url;
    link.download = attachment.name;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <div className={ `message-list--item ${ isMy ? 'my-message' : '' }` }>
        <div className="item-avatar">
          <UserAvatar sizeClass={'small'} avatarUrl={ avatarUrl } hasVideo={ false }/>
        </div>
        <div className="item-info">
          <div className="item-info--date">
            { name }, { dayjs(message_time).format('hh:mm A') }
          </div>
          {message && (
            <div className="item-info--message">
              <Linkify options={ { target: '_blank' } }>{ message }</Linkify>
            </div>
          )}
          {attachments && attachments.length > 0 && (
            <div className="body-files">
              {attachments.map((attachment: AttachmentType, index: number) => {
                const isImage = isImageFile(attachment.type);

                return (
                  <div
                    key={`${attachment.name}-${index}`}
                    className="body-files__item file-item"
                    onClick={() => handleFileClick(attachment)}
                  >
                    <div
                      className="file-item__file"
                      style={{ cursor: 'pointer' }}
                    >
                      {isImage ? (
                        <img
                          src={attachment.url}
                          alt={attachment.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        getFileIcon(attachment)
                      )}
                    </div>

                    <div className="file-item__info">
                      <CustomTooltip title={attachment.name}>
                        <div className="file-item__name">
                          {truncateText(attachment.name, 12)}
                        </div>
                      </CustomTooltip>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <PreviewFileModal
        open={previewModalOpen}
        imageUrls={imageUrls}
        imageNames={imageNames}
        currentIndex={currentImageIndex}
        onIndexChange={setCurrentImageIndex}
        onCancel={() => {
          setPreviewModalOpen(false);
          setCurrentImageIndex(0);
        }}
      />
    </>
  );
};
